/**
 * Session stash for the test slug a visitor was on when they hit "Start
 * placement" while signed out. The auth-gate round trip (magic link / OTP)
 * loses component state, so the slug rides sessionStorage and the gate's
 * post-auth auto-start forwards it to POST /v1/diagnostic/start as
 * `test_slug` (tailoring the placement to that test's coverage-map profile).
 *
 * Lifecycle: stashed by useStartDiagnostic's signed-out branch (only when a
 * test slug is in play — a generic start CLEARS it so a later placement from
 * the dashboard can't inherit a stale SAT profile), consumed exactly once by
 * the first reader (auth-gate auto-start or the signed-in start path).
 */

const KEY = "sa_diag_test_slug";

export function stashDiagnosticTestSlug(slug: string): void {
  try {
    sessionStorage.setItem(KEY, slug);
  } catch {
    // Storage unavailable (private mode quota, disabled) — the run simply
    // starts unprofiled; never block the placement on this.
  }
}

export function clearDiagnosticTestSlug(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}

/**
 * Read the stashed slug WITHOUT clearing it. Use when something needs to know
 * the funnel slug but must NOT consume it — the auth-gate's post-auth
 * auto-start still needs to read-and-clear it afterwards (e.g. the lead-magnet
 * check at OTP-verify time, which fires mid-flow before completeAuth runs).
 */
export function peekDiagnosticTestSlug(): string | null {
  try {
    return sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
}

/** Read the stashed slug and clear it (consume-once semantics). */
export function readAndClearDiagnosticTestSlug(): string | null {
  try {
    const v = sessionStorage.getItem(KEY);
    if (v) sessionStorage.removeItem(KEY);
    return v;
  } catch {
    return null;
  }
}
